import { html, setHtml } from "./html.js";
import { L } from "./vendor.js";
import {
  announceGreatLakesLayer,
  clearGreatLakesVisuals,
  createParticleLayer,
  createWindArrowLayer,
  createWaveArrowLayer,
  greatLakesConditionsLayer,
  greatLakesControlValue,
  greatLakesAirTemperatureRasters,
  greatLakesMaxForecastHour,
  noaaDataStatus,
  renderGreatLakesCurrents,
  setCurrentLegendRange,
  setCurrentSpeedMax,
  setGreatLakesStatus,
  setTemperatureLegendRange,
  setAirTemperatureForecastHorizon,
  setThermoclineLegendRange,
  setWaveLegendRange,
  setWindLegendRange,
  showPaletteRange,
  waveParticleFields,
  savedWaveParticleColor,
  waveParticleColorParts,
  savedWindParticleColor,
  windParticleColorParts
} from "./great-lakes-conditions.js";
import { createPaletteFilter, fitPaletteToView, paletteOverlays } from "./great-lakes-palette.js";
import { createScalarRasterTransition } from "./thermocline-flow.js";
import { windParticleFields } from "./wind-fields.js";
import { formatAppDate, formatAppTime } from "./app-time.js";

// Forecast animation steps through hourly frames on one shared colour scale.
// Scalar rasters interpolate their encoded values before the palette colours
// them, so fronts and gradients move continuously between forecast hours.
// Particle layers interpolate their fields on the same frame clock. The first
// share of frame data is loaded before playback; later frames load ahead.
// Time per frame and the share of it spent fading into the next, per speed.
export const ANIMATION_SPEEDS = Object.freeze({
  slow: { label: "0.5×", frameMs: 2800 },
  normal: { label: "1×", frameMs: 1400 },
  fast: { label: "2×", frameMs: 700 },
  faster: { label: "3×", frameMs: 467 },
  fastest: { label: "4×", frameMs: 350 }
});
const ANIMATION_SPEED_CYCLE = ["normal", "fast", "faster", "fastest", "slow"];
export const ANIMATION_FADE_SHARE = 1;
export const ANIMATION_SPEED_STORAGE_KEY = "glc.AnimationSpeed";
export const ANIMATION_POLL_MS = 1500;
const REPLACEMENT_CHECK_MS = 15000;
export const ANIMATION_FETCH_CONCURRENCY = 4;
const INITIAL_FRAME_LOAD_FRACTION = 0.4;
const FRAME_LOOKAHEAD = 4;
export const ANIMATION_IDLE_LABEL = "Play up to 5 days";
export const WAVE_ANIMATION_IDLE_LABEL = "Play up to 48 hours";
// Frames sit in their own pane, just under the overlay pane so current
// particles stay on top. The pane carries the layer's opacity, so each image
// can be fully opaque and a frame fading in over another never lets the map
// show through.
const ANIMATION_PANE = "greatLakesAnimation";
const RASTER_STYLES = {
  temperature: { className: "great-lakes-temperature-raster", opacity: 0.9 },
  "air-temperature": { className: "great-lakes-temperature-raster", opacity: 0.9 },
  thermocline: { className: "great-lakes-thermocline-raster", opacity: 0.88 },
  currents: { className: "great-lakes-current-raster", opacity: 0.8 },
  waves: { className: "great-lakes-wave-raster", opacity: 0.86 },
  wind: { className: "great-lakes-wind-raster", opacity: 0.8 },
  upwelling: { className: "great-lakes-upwelling-raster", opacity: 0.88 }
};

const state = {
  active: false,
  playing: false,
  layer: "",
  depth: 0,
  frames: [],
  payloads: [],
  backgroundPayloads: null,
  overlays: [],
  index: 0,
  timer: null,
  sliderFrame: null,
  fadeTimer: null,
  expiryTimer: null,
  replacementTimer: null,
  replacementRevision: 0,
  // Every frame's colours fit what is on screen across all frames (great-lakes-palette.js).
  paletteFit: null,
  rasterTransition: null,
  preparedRasterTransition: null,
  rasterImageCache: new Map(),
  stack: 0,
  particles: null,
  arrows: null,
  previousArrows: null,
  waveArrows: null,
  previousWaveArrows: null,
  waveArrowTimer: null,
  map: null,
  rasterPayloads: [],
  paletteFilter: null,
  rasterStyle: null,
  compactFrameLayers: false,
  loadContext: null,
  loadRevision: 0,
  frameLoads: new Map(),
  frameImageLoads: new Map(),
  preloadedFrames: new Set(),
  backgroundFramesByHour: new Map(),
  waterMaskCaches: null
};
const payloadCache = new Map();
const PAYLOAD_CACHE_LIMIT = 80;

function compactAnimationMode() {
  return Boolean(window.matchMedia?.("(pointer: coarse) and (max-width: 1024px)")?.matches);
}

export function savedAnimationSpeed() {
  try {
    const saved = localStorage.getItem(ANIMATION_SPEED_STORAGE_KEY);
    return Object.hasOwn(ANIMATION_SPEEDS, saved) ? saved : "normal";
  } catch { return "normal"; }
}

function speedTiming() {
  const choice = document.querySelector("[data-gl-animation-speed-choice]");
  const speed = ANIMATION_SPEEDS[choice?.value] || ANIMATION_SPEEDS[savedAnimationSpeed()];
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  return { frameMs: speed.frameMs, fadeMs: reduced ? 0 : Math.round(speed.frameMs * ANIMATION_FADE_SHARE) };
}

export function greatLakesAnimationActive() {
  return state.active;
}

export function pauseGreatLakesAnimation() {
  if (!state.playing) return;
  state.playing = false;
  clearTimeout(state.timer);
  cancelAnimationFrame(state.sliderFrame);
  state.sliderFrame = null;
  clearTimeout(state.fadeTimer);
  clearTimeout(state.waveArrowTimer);
  state.waveArrowTimer = null;
  state.previousWaveArrows?.remove();
  state.previousWaveArrows = null;
  state.waveArrows?.setOpacity?.(1);
  state.previousArrows?.remove();
  state.previousArrows = null;
  setMarkerGroupOpacity(state.arrows, 1);
  state.map?.getPane(ANIMATION_PANE)?.classList.add("is-instant");
  showFrame(state.index, { fade: false });
  syncTimelineControls();
}

// The hour the map is showing, for readings at a clicked point.
export function greatLakesAnimationForecastHour() {
  return state.active && state.frames[state.index] ? String(state.frames[state.index].forecastHour) : null;
}

export function greatLakesAnimationPointParams() {
  const frame = state.active && state.frames[state.index];
  if (!frame) return {};
  const query = new URLSearchParams((frame.url || "").split("?")[1] || "");
  const identifier = query.get("frame");
  return identifier ? { frame: identifier, inputs: query.get("inputs") || undefined } : {};
}

export function greatLakesTimelineHtml() {
  const speed = savedAnimationSpeed();
  const speedLabel = ANIMATION_SPEEDS[speed].label;
  return html`<div class="great-lakes-timeline" data-gl-timeline>
    <button type="button" class="great-lakes-play" data-gl-play aria-label="Play the forecast" aria-pressed="false" title="Play the forecast">${playIconHtml(false)}</button>
    <button type="button" class="great-lakes-timeline-speed" data-gl-animation-speed-choice value="${speed}" aria-label="Animation speed: ${speedLabel}. Click to change." title="Change animation speed">${speedLabel}</button>
    <div class="great-lakes-timeline-track">
      <input type="range" data-gl-frame min="0" max="40" step="1" value="0" disabled aria-label="Forecast time" />
      <div class="great-lakes-timeline-meta">
        <span class="great-lakes-timeline-label" data-gl-frame-label aria-live="off">${ANIMATION_IDLE_LABEL}</span>
      </div>
    </div>
    <button type="button" class="great-lakes-timeline-stop" data-gl-animation-stop aria-label="Stop the animation" title="Back to the forecast choice" hidden><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 7 10 10m0-10L7 17"/></svg></button>
  </div>`;
}

function playIconHtml(playing) {
  return playing
    ? html`<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>`
    : html`<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.2-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5Z"/></svg>`;
}

// "Now · 7 PM", "Sat 3 AM": short enough to sit under the slider.
export function animationFrameLabel(validTime, index = 0) {
  const date = new Date(validTime);
  if (Number.isNaN(date.getTime())) return index === 0 ? "Now" : "";
  const time = formatAppTime(date, date.getUTCMinutes() ? {} : { minute: undefined });
  if (index === 0) return `Now · ${time}`;
  return `${formatAppDate(date, { weekday: "short", month: undefined, day: undefined })} ${time}`;
}

// The frame nearest a valid time, so a reload (new layer, depth, or data) keeps the time on screen.
export function nearestFrameIndex(frames, validTime) {
  const target = Date.parse(validTime);
  if (!Number.isFinite(target) || !frames.length) return 0;
  let best = 0;
  frames.forEach((frame, index) => {
    if (Math.abs(Date.parse(frame.validTime) - target) < Math.abs(Date.parse(frames[best].validTime) - target)) best = index;
  });
  return best;
}

export function setupGreatLakesTimeline(host, reload) {
  const play = host.querySelector("[data-gl-play]");
  const slider = host.querySelector("[data-gl-frame]");
  const stop = host.querySelector("[data-gl-animation-stop]");
  if (!play || !slider || !stop) return;
  document.addEventListener("great-lakes-wind-color-changed", (event) => {
    if (state.layer === "wind") state.particles?.setParticleColor(windParticleColorParts(event.detail?.color));
  });
  document.addEventListener("great-lakes-wave-color-changed", (event) => {
    if (state.layer === "waves") state.particles?.setParticleColor(waveParticleColorParts(event.detail?.color));
  });
  play.addEventListener("click", () => {
    if (!state.active) {
      state.active = true;
      state.playing = true;
      syncTimelineControls();
      reload();
      return;
    }
    if (state.playing) pauseGreatLakesAnimation();
    else {
      state.playing = true;
      syncTimelineControls();
      scheduleNextFrame();
    }
  });
  slider.addEventListener("input", () => {
    if (!state.frames.length) return;
    state.playing = false;
    clearTimeout(state.timer);
    cancelAnimationFrame(state.sliderFrame);
    state.sliderFrame = null;
    showFrame(Math.round(Number(slider.value)));
    syncTimelineControls();
  });
  stop.addEventListener("click", () => {
    stopGreatLakesAnimation();
    reload();
  });
  host.querySelector("[data-gl-animation-speed-choice]")?.addEventListener("click", (event) => {
    const button = event.currentTarget;
    const speedIndex = ANIMATION_SPEED_CYCLE.indexOf(button.value);
    const speed = ANIMATION_SPEED_CYCLE[(speedIndex + 1) % ANIMATION_SPEED_CYCLE.length];
    const label = ANIMATION_SPEEDS[speed].label;
    button.value = speed;
    button.textContent = label;
    button.setAttribute("aria-label", `Animation speed: ${label}. Click to change.`);
    button.title = `Animation speed: ${label}`;
    try { localStorage.setItem(ANIMATION_SPEED_STORAGE_KEY, speed); } catch { /* storage unavailable */ }
    applyFadeDuration();
    if (state.playing) scheduleNextFrame();
  });
  document.addEventListener("visibilitychange", () => { if (!document.hidden && state.playing) scheduleNextFrame(); });
}

function applyFadeDuration() {
  const duration = `${speedTiming().fadeMs}ms`;
  state.map?.getPane(ANIMATION_PANE)?.style.setProperty("--gl-frame-fade", duration);
  state.map?.getContainer()?.style.setProperty("--gl-frame-fade", duration);
}

export function syncTimelineControls() {
  document.dispatchEvent(new CustomEvent("great-lakes-animation-state", { detail: { active: state.active } }));
  const play = document.querySelector("[data-gl-play]");
  const slider = document.querySelector("[data-gl-frame]");
  const stop = document.querySelector("[data-gl-animation-stop]");
  const label = document.querySelector("[data-gl-frame-label]");
  document.querySelector("[data-gl-timeline]")?.classList.toggle("is-active", state.active);
  if (play) {
    setHtml(play, playIconHtml(state.playing));
    play.setAttribute("aria-pressed", String(state.playing));
    const action = state.playing ? "Pause the forecast" : "Play the forecast";
    play.setAttribute("aria-label", action);
    play.title = action;
  }
  if (stop) stop.hidden = !state.active;
  if (slider) {
    slider.disabled = !state.frames.length;
    slider.max = String(Math.max(0, state.frames.length - 1));
    slider.step = state.playing ? "any" : "1";
    slider.value = String(state.index);
  }
  if (label && !state.active) {
    const layer = greatLakesControlValue("layer");
    label.textContent = layer === "air-temperature" ? `Play up to ${greatLakesMaxForecastHour(layer)} hours`
      : layer === "waves" ? WAVE_ANIMATION_IDLE_LABEL : ANIMATION_IDLE_LABEL;
  }
}

export function stopGreatLakesAnimation() {
  state.active = false;
  state.playing = false;
  clearTimeout(state.timer);
  clearTimeout(state.expiryTimer);
  state.expiryTimer = null;
  cancelAnimationFrame(state.sliderFrame);
  state.sliderFrame = null;
  clearGreatLakesAnimationVisuals();
  state.frames = [];
  state.payloads = [];
  state.backgroundPayloads = null;
  state.index = 0;
  syncTimelineControls();
}

// Called by clearGreatLakesVisuals: the overlays themselves live in the shared layer group.
export function clearGreatLakesAnimationVisuals() {
  clearReplacementCheck();
  state.loadRevision += 1;
  state.loadContext = null;
  state.frameLoads.clear();
  state.frameImageLoads.clear();
  state.preloadedFrames.clear();
  state.backgroundFramesByHour.clear();
  state.waterMaskCaches?.bits.clear();
  state.waterMaskCaches?.rings.clear();
  state.waterMaskCaches = null;
  clearTimeout(state.timer);
  clearTimeout(state.expiryTimer);
  state.expiryTimer = null;
  clearTimeout(state.fadeTimer);
  clearTimeout(state.waveArrowTimer);
  cancelAnimationFrame(state.sliderFrame);
  state.sliderFrame = null;
  state.particles?.remove();
  state.particles = null;
  state.arrows?.remove();
  state.arrows = null;
  state.previousArrows?.remove();
  state.previousArrows = null;
  state.waveArrows?.remove();
  state.waveArrows = null;
  state.previousWaveArrows?.remove();
  state.previousWaveArrows = null;
  state.paletteFit?.stop();
  state.paletteFit = null;
  const rasterTransition = state.rasterTransition;
  const preparedRasterTransition = state.preparedRasterTransition;
  rasterTransition?.remove();
  state.rasterTransition = null;
  if (preparedRasterTransition?.transition !== rasterTransition) preparedRasterTransition?.transition?.remove();
  state.preparedRasterTransition = null;
  state.rasterImageCache.clear();
  if (state.compactFrameLayers) {
    state.overlays.forEach((overlays) => overlays?.forEach((overlay) => greatLakesConditionsLayer.removeLayer(overlay)));
  }
  state.overlays = [];
  state.rasterPayloads = [];
  state.paletteFilter = null;
  state.rasterStyle = null;
  state.compactFrameLayers = false;
}

function sleep(milliseconds, signal) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, milliseconds);
    signal?.addEventListener("abort", () => { clearTimeout(timer); reject(new DOMException("Aborted", "AbortError")); }, { once: true });
  });
}

async function mapLimit(items, limit, worker) {
  const results = new Array(items.length);
  let next = 0;
  const run = async () => {
    while (next < items.length) {
      const index = next;
      next += 1;
      results[index] = await worker(items[index], index);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, run));
  return results;
}

// The server may still be drawing a requested animation; poll until it is ready.
export function airTemperatureAnimationFrames(index) {
  const maximum = Number(index.maxForecastHour ?? index.metadata?.maxForecastHour ?? 48);
  const seenHours = new Set(), seenTimes = new Set();
  return (index.frames || []).filter((frame) => {
    const hour = Number(frame.forecastHour);
    if (!Number.isFinite(hour) || hour < 0 || hour > Math.min(48, maximum) || seenHours.has(hour)
      || (frame.validTime && seenTimes.has(frame.validTime))) return false;
    seenHours.add(hour);
    if (frame.validTime) seenTimes.add(frame.validTime);
    return true;
  });
}

async function animationIndex(layer, depth, signal, isCurrent) {
  for (;;) {
    const index = await window.noaaGreatLakesApi.animation({ layer, depth, signal, refresh: true });
    if (index.ready) {
      if (layer !== "air-temperature") return index;
      const frames = airTemperatureAnimationFrames(index);
      if (!frames.length) throw new Error("No HRRR forecast frames are available");
      setAirTemperatureForecastHorizon({ maxForecastHour: Math.max(...frames.map((frame) => Number(frame.forecastHour))) });
      return { ...index, frames };
    }
    if (index.error) throw new Error(index.error);
    if (!isCurrent()) throw new DOMException("Superseded", "AbortError");
    const { done = 0, total = 0 } = index.progress || {};
    setGreatLakesStatus(`Loading the forecast animation… ${total ? Math.round((done / total) * 100) : 0}%`);
    await sleep(ANIMATION_POLL_MS, signal);
  }
}

// A slow image must not hold up the whole animation; it finishes loading on screen.
const IMAGE_PRELOAD_LIMIT_MS = 10000;

function animationRasters(payload) {
  return state.layer === "air-temperature" ? greatLakesAirTemperatureRasters(payload) : payload?.rasters || [];
}

function preloadImages(payload) {
  const urls = animationRasters(payload).flatMap((raster) => raster.valueUrl ? [raster.valueUrl, raster.mixedUrl].filter(Boolean) : [raster.imageUrl]);
  return Promise.all(urls.map((url) => new Promise((resolve) => {
    const image = new Image();
    const timer = setTimeout(resolve, IMAGE_PRELOAD_LIMIT_MS);
    image.onload = image.onerror = () => { clearTimeout(timer); resolve(); };
    image.src = url;
  })));
}

function compactWaterMask(field, caches) {
  const waterMask = field.waterMask;
  if (!waterMask?.bits || !caches) return waterMask;
  let modelBits = caches.bits.get(field.model);
  if (!modelBits) {
    modelBits = new Map();
    caches.bits.set(field.model, modelBits);
  }
  let bits = modelBits.get(waterMask.bits);
  if (!bits) {
    const binary = atob(waterMask.bits);
    bits = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) bits[index] = binary.charCodeAt(index);
    modelBits.set(waterMask.bits, bits);
  }
  let rings = waterMask.rings;
  if (rings?.length) {
    const ringKey = [field.model, waterMask.rows, waterMask.columns, waterMask.latitudeStart,
      waterMask.latitudeEnd, waterMask.longitudeStart, waterMask.longitudeEnd].join("|");
    if (caches.rings.has(ringKey)) rings = caches.rings.get(ringKey);
    else caches.rings.set(ringKey, rings);
  }
  return { ...waterMask, bits, ...(rings ? { rings } : {}) };
}

function compactCurrentPayload(payload, display, waterMaskCaches) {
  if (display === "flow") {
    return {
      ...payload,
      data: [],
      fields: (payload.fields || []).map((field) => ({
        ...field,
        waterMask: compactWaterMask(field, waterMaskCaches),
        latitudeAxis: Float32Array.from(field.latitudeAxis || []),
        longitudeAxis: Float32Array.from(field.longitudeAxis || []),
        mask: Uint8Array.from(field.mask || []),
        u: Float32Array.from(field.u || []),
        v: Float32Array.from(field.v || [])
      }))
    };
  }
  return { ...payload, fields: [], ...(display === "off" ? { data: [] } : {}) };
}

async function framePayload(url, signal, { layer, currentDisplay, compact = false, waterMaskCaches, preload = true } = {}) {
  const cacheKey = layer === "currents" ? `${url}|${currentDisplay}|${compact ? "compact" : "full"}` : url;
  const cached = payloadCache.get(cacheKey);
  if (cached) {
    if (preload) await preloadImages(cached);
    return cached;
  }
  const response = await window.noaaGreatLakesApi.animationFrame({ url, signal });
  const payload = layer === "currents" && compact ? compactCurrentPayload(response, currentDisplay, waterMaskCaches) : response;
  if (preload) await preloadImages(payload);
  payloadCache.set(cacheKey, payload);
  while (payloadCache.size > PAYLOAD_CACHE_LIMIT) payloadCache.delete(payloadCache.keys().next().value);
  return payload;
}

function animationContextIsCurrent(context) {
  return Boolean(context && state.loadContext === context && context.revision === state.loadRevision
    && state.active && !context.signal?.aborted && (context.isCurrent?.() ?? true));
}

function framePayloadIsLoaded(index) {
  return Boolean(state.payloads[index]
    && (!state.backgroundFramesByHour.has(Number(state.frames[index]?.forecastHour)) || state.backgroundPayloads?.[index]));
}

async function loadFrameData(index) {
  const context = state.loadContext;
  if (!animationContextIsCurrent(context)) throw new DOMException("Superseded", "AbortError");
  if (framePayloadIsLoaded(index)) return state.payloads[index];
  const pending = state.frameLoads.get(index);
  if (pending) return pending;
  const frame = state.frames[index];
  if (!frame) throw new RangeError("Invalid forecast animation frame");
  const backgroundFrame = state.backgroundFramesByHour.get(Number(frame.forecastHour));
  const loading = (async () => {
    const [payload, backgroundPayload] = await Promise.all([
      framePayload(frame.url, context.signal, {
        layer: context.layer,
        currentDisplay: context.currentDisplay,
        compact: context.compactMode,
        waterMaskCaches: context.waterMaskCaches,
        preload: false
      }),
      backgroundFrame ? framePayload(backgroundFrame.url, context.signal, {
        layer: "temperature",
        currentDisplay: context.currentDisplay,
        compact: context.compactMode,
        waterMaskCaches: context.waterMaskCaches,
        preload: false
      }) : null
    ]);
    if (!animationContextIsCurrent(context)) throw new DOMException("Superseded", "AbortError");
    state.payloads[index] = payload;
    if (state.backgroundPayloads && backgroundPayload) state.backgroundPayloads[index] = backgroundPayload;
    if (state.rasterPayloads.length === state.frames.length) {
      state.rasterPayloads[index] = context.layer === "currents"
        ? (context.background === "temperature" ? backgroundPayload
          : context.background === "none" ? null : payload)
        : payload;
    }
    state.paletteFit?.addRasters(state.rasterPayloads.map(animationRasters));
    return payload;
  })();
  state.frameLoads.set(index, loading);
  try {
    return await loading;
  } finally {
    if (state.frameLoads.get(index) === loading) state.frameLoads.delete(index);
  }
}

async function preloadFrameImages(index) {
  const context = state.loadContext;
  if (!animationContextIsCurrent(context)) throw new DOMException("Superseded", "AbortError");
  if (state.preloadedFrames.has(index)) return;
  const pending = state.frameImageLoads.get(index);
  if (pending) return pending;
  const loading = (async () => {
    const payload = await loadFrameData(index);
    const imagePayloads = context.layer === "currents"
      ? (context.background === "temperature" ? [state.backgroundPayloads?.[index]]
        : context.background === "none" ? [] : [payload])
      : [payload];
    await Promise.all(imagePayloads.filter(Boolean).map(preloadImages));
    if (animationContextIsCurrent(context)) state.preloadedFrames.add(index);
  })();
  state.frameImageLoads.set(index, loading);
  try {
    await loading;
  } finally {
    if (state.frameImageLoads.get(index) === loading) state.frameImageLoads.delete(index);
  }
}

function hasScalarRasterFrame(index) {
  return Boolean(animationRasters(state.rasterPayloads[index]).some((raster) => raster.valueUrl && raster.valueRange));
}

function canInterpolateRasterPair(fromIndex, toIndex) {
  return hasScalarRasterFrame(fromIndex) && hasScalarRasterFrame(toIndex);
}

function prepareScalarRasterTransition(fromIndex, toIndex) {
  const active = state.rasterTransition;
  if (active?.fromIndex === fromIndex && active?.toIndex === toIndex) return Promise.resolve(active);
  const key = `${fromIndex}:${toIndex}`;
  const prepared = state.preparedRasterTransition;
  if (prepared?.key === key) return prepared.promise || Promise.resolve(prepared.transition);
  prepared?.transition?.remove();

  const context = state.loadContext;
  const record = { key, fromIndex, toIndex, promise: null, transition: null, waiting: false };
  state.preparedRasterTransition = record;
  record.promise = createScalarRasterTransition(
    animationRasters(state.rasterPayloads[fromIndex]),
    animationRasters(state.rasterPayloads[toIndex]),
    {
      pane: ANIMATION_PANE,
      interactive: false,
      colorizeValues: state.layer === "air-temperature",
      imageCache: state.rasterImageCache,
      className: `${state.rasterStyle?.className || ""} great-lakes-animation-frame`
    },
    state.paletteFilter
  ).then((transition) => {
    if (!transition) {
      record.promise = null;
      return null;
    }
    if (state.preparedRasterTransition !== record || !animationContextIsCurrent(context)) {
      transition.remove();
      return null;
    }
    transition.fromIndex = fromIndex;
    transition.toIndex = toIndex;
    record.transition = transition;
    record.promise = null;
    return transition;
  }).catch((error) => {
    if (state.preparedRasterTransition === record) {
      record.error = error;
      record.promise = null;
    }
    return null;
  });
  return record.promise;
}

function prefetchScalarRasterTransition(fromIndex, toIndex) {
  if (!state.frames.length) return;
  const context = state.loadContext;
  void (async () => {
    await loadFrameData(toIndex);
    await preloadFrameImages(toIndex);
    if (!animationContextIsCurrent(context) || !canInterpolateRasterPair(fromIndex, toIndex)) return;
    await prepareScalarRasterTransition(fromIndex, toIndex);
  })().catch(() => {});
}

function prefetchAhead(index) {
  const context = state.loadContext;
  if (!animationContextIsCurrent(context) || !state.frames.length) return;
  const nextFrame = (index + 1) % state.frames.length;
  const targets = Array.from({ length: Math.min(FRAME_LOOKAHEAD, state.frames.length - 1) }, (_, offset) =>
    (index + offset + 1) % state.frames.length
  );
  void mapLimit(targets, context.compactMode ? 2 : ANIMATION_FETCH_CONCURRENCY, async (frameIndex) => {
    if (!animationContextIsCurrent(context)) return;
    await loadFrameData(frameIndex);
    await preloadFrameImages(frameIndex);
    if (frameIndex === nextFrame && canInterpolateRasterPair(index, frameIndex)) {
      void prepareScalarRasterTransition(index, frameIndex);
    }
  }).catch(() => {});
}

function reportFrameLoadError(index, error, context) {
  if (error?.name === "AbortError" || !animationContextIsCurrent(context) || state.index !== index) return;
  state.playing = false;
  syncTimelineControls();
  setGreatLakesStatus(error.message?.startsWith("Lakes-only air temperature")
    ? error.message : "This forecast frame could not be loaded. Try again in a moment.", true);
}

function scheduleFrameExpiry(reload) {
  clearTimeout(state.expiryTimer);
  state.expiryTimer = null;
  if (!reload || !state.active) return;
  const nextExpiry = state.frames
    .map((frame) => Date.parse(frame.validTime || ""))
    .filter((timestamp) => Number.isFinite(timestamp) && timestamp > Date.now())
    .sort((left, right) => left - right)[0];
  if (!nextExpiry) return;
  state.expiryTimer = setTimeout(() => {
    state.expiryTimer = null;
    if (state.active) reload();
  }, Math.max(1000, nextExpiry - Date.now() + 100));
}

function clearReplacementCheck() {
  clearTimeout(state.replacementTimer);
  state.replacementTimer = null;
  state.replacementRevision += 1;
}

function scheduleReplacementCheck({ layer, depth, index, temperatureIndex, signal, isCurrent, reload }) {
  if (!reload || (!index.fallback && !temperatureIndex?.fallback)) return;
  const revision = state.replacementRevision;
  const current = () => state.active && state.replacementRevision === revision && isCurrent() && !signal?.aborted;
  const choices = [];
  if (index.fallback) choices.push({ layer, index });
  if (temperatureIndex?.fallback) choices.push({ layer: "temperature", index: temperatureIndex });
  state.replacementTimer = setTimeout(async () => {
    state.replacementTimer = null;
    if (!current()) return;
    // Probe just the small indexes while the saved frames keep playing.
    const updates = await Promise.allSettled(choices.map((choice) => window.noaaGreatLakesApi.animation({
      layer: choice.layer, depth, signal, refresh: true
    })));
    if (!current()) return;
    const changed = updates.some((update) => {
      if (update.status !== "fulfilled") return false;
      const latest = update.value;
      return latest.ready && !latest.fallback && latest.frames?.length;
    });
    if (changed) reload();
    else scheduleReplacementCheck({ layer, depth, index, temperatureIndex, signal, isCurrent, reload });
  }, REPLACEMENT_CHECK_MS);
}

export async function loadGreatLakesAnimation(map, { layer, depth, signal, isCurrent, reload }) {
  clearReplacementCheck();
  state.map = map;
  clearTimeout(state.timer);
  clearTimeout(state.expiryTimer);
  state.expiryTimer = null;
  const shownTime = state.frames[state.index]?.validTime;
  const background = layer === "currents" ? greatLakesControlValue("current-background") : "";
  const currentDisplay = layer === "currents" ? greatLakesControlValue("current-display") : "";
  let compactMode = compactAnimationMode();
  const waterMaskCaches = { bits: new Map(), rings: new Map() };
  setGreatLakesStatus("Loading the forecast animation…");
  try {
    const [index, temperatureIndex] = await Promise.all([
      animationIndex(layer, depth, signal, isCurrent),
      background === "temperature" ? animationIndex("temperature", depth, signal, isCurrent) : null
    ]);
    if (!isCurrent()) return false;
    compactMode ||= index.frames.length > 3 || (temperatureIndex?.frames.length || 0) > 3;
    if (compactMode) {
      payloadCache.clear();
    }
    clearGreatLakesVisuals();
    state.layer = layer;
    state.depth = Number(depth) || 0;
    state.frames = index.frames;
    state.payloads = new Array(index.frames.length).fill(null);
    state.backgroundFramesByHour = new Map((temperatureIndex?.frames || []).map((frame) => [Number(frame.forecastHour), frame]));
    state.backgroundPayloads = temperatureIndex ? new Array(index.frames.length).fill(null) : null;
    state.index = shownTime ? nearestFrameIndex(state.frames, shownTime) : 0;
    state.waterMaskCaches = waterMaskCaches;
    const context = {
      revision: state.loadRevision,
      signal,
      isCurrent,
      layer,
      currentDisplay,
      background,
      compactMode,
      waterMaskCaches
    };
    state.loadContext = context;
    // Air forecasts are short: warm every image to avoid network stalls during
    // playback. Longer lake forecasts keep their bounded loading window.
    const initialCount = layer === "air-temperature" ? index.frames.length
      : Math.ceil(index.frames.length * INITIAL_FRAME_LOAD_FRACTION);
    const initialIndices = Array.from({ length: initialCount }, (_, offset) =>
      (state.index + offset) % index.frames.length
    );
    let loaded = 0;
    await mapLimit(initialIndices, compactMode ? 2 : ANIMATION_FETCH_CONCURRENCY, async (frameIndex) => {
      await preloadFrameImages(frameIndex);
      loaded += 1;
      if (animationContextIsCurrent(context)) {
        setGreatLakesStatus(`Loading the forecast animation… ${loaded} of ${initialIndices.length}`);
      }
    });
    if (!animationContextIsCurrent(context)) return false;
    await preloadFrameImages(state.index);
    if (!animationContextIsCurrent(context)) return false;
    const first = state.payloads[state.index]?.metadata || state.payloads.find(Boolean)?.metadata || {};
    if (["temperature", "bottom-temperature", "air-temperature"].includes(layer)) {
      setTemperatureLegendRange(first, layer === "air-temperature" ? "airTemperature" : "waterTemperature");
    }
    else if (layer === "thermocline") setThermoclineLegendRange(first);
    else if (layer === "waves") setWaveLegendRange(first);
    else if (layer === "wind") setWindLegendRange(first);
    else if (layer === "upwelling") { /* fixed legend */ }
    else {
      setCurrentLegendRange(first);
      setCurrentSpeedMax(first);
      if (state.backgroundPayloads) {
        setTemperatureLegendRange(state.backgroundPayloads[state.index]?.metadata
          || state.backgroundPayloads.find(Boolean)?.metadata || {});
      }
    }
    buildFrames(map, background, compactMode);
    showFrame(state.index, { fade: false });
    scheduleFrameExpiry(reload);
    scheduleReplacementCheck({ layer, depth, index, temperatureIndex, signal, isCurrent, reload });
    const shownMetadata = state.payloads[state.index]?.metadata || {};
    announceGreatLakesLayer(layer, shownMetadata,
      state.backgroundPayloads?.[state.index]?.metadata || (layer === "temperature" ? shownMetadata : null));
    syncTimelineControls();
    if (state.playing) scheduleNextFrame();
    return true;
  } catch (error) {
    if (error.name !== "AbortError" && isCurrent()) {
      stopGreatLakesAnimation();
      setGreatLakesStatus(error.message.startsWith("Lakes-only air temperature")
        ? error.message : "NOAA data for the forecast animation is unavailable right now.", true);
    }
    return false;
  }
}

function buildFrames(map, background, compactMode = compactAnimationMode()) {
  const layer = state.layer;
  const initialPayload = state.payloads[state.index];
  if (layer === "wind") {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const display = greatLakesControlValue("wind-display");
    if (display === "flow" && !reduced && initialPayload?.points?.length) {
      state.particles = createParticleLayer(map, windParticleFields(initialPayload.points), {
        clipToGreatLakesWater: true,
        particleColor: windParticleColorParts(savedWindParticleColor())
      }).addTo(map);
    } else if (display === "arrows" && initialPayload?.points?.length) state.arrows = createWindArrowLayer(map, initialPayload.points).addTo(map);
  }
  const rasterPayloads = layer === "currents"
    ? (background === "temperature" ? state.backgroundPayloads : background === "none" ? null : state.payloads)
    : state.payloads;
  const kind = (layer === "currents" && background === "temperature") || layer === "bottom-temperature" ? "temperature" : layer;
  const style = RASTER_STYLES[kind];
  const pane = map.getPane(ANIMATION_PANE) || map.createPane(ANIMATION_PANE);
  pane.style.zIndex = "395";
  pane.style.pointerEvents = "none";
  pane.style.opacity = String(style.opacity);
  applyFadeDuration();
  state.stack = 0;
  const filter = createPaletteFilter({ smooth: ["temperature", "air-temperature", "waves", "wind"].includes(kind) });
  state.rasterPayloads = rasterPayloads || [];
  state.paletteFilter = filter;
  state.rasterStyle = style;
  state.compactFrameLayers = compactMode;
  state.overlays = new Array(state.frames.length).fill(null);
  const frameRasters = state.payloads.map((_, index) => animationRasters(rasterPayloads?.[index]));
  state.paletteFit = fitPaletteToView(map, frameRasters, kind, filter, (low, high) => {
    showPaletteRange(kind, low, high);
  });
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (layer === "waves") {
    if (greatLakesControlValue("wave-display") === "flow" && !reduced && initialPayload?.arrows?.length) {
      state.particles = createParticleLayer(map, waveParticleFields(initialPayload.arrows), {
        clipToGreatLakesWater: true,
        particleColor: waveParticleColorParts(savedWaveParticleColor())
      }).addTo(map);
    }
    return;
  }
  if (layer !== "currents") return;
  const display = greatLakesControlValue("current-display");
  if (display === "flow" && !reduced && (initialPayload?.fields || []).length) {
    state.particles = createParticleLayer(map, initialPayload.fields).addTo(map);
  } else if (display !== "off") {
    state.arrows = L.layerGroup().addTo(map);
  }
}

function createAnimationFrameOverlays(index) {
  const rasters = animationRasters(state.rasterPayloads[index]);
  const overlays = paletteOverlays(rasters, state.paletteFilter, {
    pane: ANIMATION_PANE,
    opacity: 0,
    interactive: false,
    className: `${state.rasterStyle?.className || ""} great-lakes-animation-frame`
  });
  overlays.forEach((overlay) => overlay.addTo(greatLakesConditionsLayer));
  return overlays;
}

function startScalarRasterTransition(transition, fromIndex, toIndex, duration) {
  if (state.rasterTransition === transition) return;
  transition.addTo(greatLakesConditionsLayer);
  state.stack += 1;
  transition.setZIndex(state.stack);
  clearTimeout(state.fadeTimer);

  state.overlays.forEach((overlays, index) => {
    if (!overlays) return;
    overlays.forEach((overlay) => greatLakesConditionsLayer.removeLayer(overlay));
    state.overlays[index] = null;
  });
  const previous = state.rasterTransition;
  state.rasterTransition = transition;
  transition.fromIndex = fromIndex;
  transition.toIndex = toIndex;
  if (state.preparedRasterTransition?.transition === transition) state.preparedRasterTransition = null;
  previous?.remove();
  transition.transition(duration);
  if (state.frames.length > 1) prefetchScalarRasterTransition(toIndex, (toIndex + 1) % state.frames.length);
}

function hideAnimationFramesExcept(index) {
  const pane = state.map?.getPane(ANIMATION_PANE);
  pane?.classList.add("is-instant");
  state.overlays.forEach((overlays, frame) => {
    if (frame === index || !overlays) return;
    if (state.compactFrameLayers) {
      overlays.forEach((overlay) => greatLakesConditionsLayer.removeLayer(overlay));
      state.overlays[frame] = null;
    } else overlays.forEach((overlay) => animateOverlayOpacity(overlay, 0));
  });
  // Commit cleanup without fading old frames out underneath the next fade.
  if (pane) void pane.offsetWidth;
}

function setMarkerGroupOpacity(group, opacity) {
  group?.eachLayer((marker) => marker.setOpacity(opacity));
}

// Animate the image element directly so every forecast layer, including the
// transparent upwelling rasters, follows the same frame clock in every browser.
// The Web Animations API lets the compositor run the fade, so it stays smooth
// while particles or frame loading keep the main thread busy. The overlay's
// own opacity is set to the target at once; the animation only covers the
// ramp, so cancelling or finishing it never leaves a stale value behind.
function animateOverlayOpacity(overlay, target, durationMs = 0) {
  const image = overlay.getElement();
  if (!image) { overlay.setOpacity(target); return; }
  // Start from what is on screen, including part way through a fade.
  const current = Number.parseFloat(getComputedStyle(image).opacity);
  cancelAnimationFrame(overlay._glOpacityFrame);
  overlay._glOpacityFrame = null;
  overlay._glOpacityAnimation?.cancel();
  overlay._glOpacityAnimation = null;
  const startOpacity = Number.isFinite(current) ? current : 0;
  if (!durationMs || Math.abs(startOpacity - target) < 0.001) {
    overlay.setOpacity(target);
    return;
  }
  if (typeof image.animate === "function") {
    overlay.setOpacity(target);
    const fade = image.animate([{ opacity: startOpacity }, { opacity: target }], { duration: durationMs, easing: "linear" });
    overlay._glOpacityAnimation = fade;
    fade.onfinish = () => { if (overlay._glOpacityAnimation === fade) overlay._glOpacityAnimation = null; };
    return;
  }
  const started = performance.now();
  const update = (now) => {
    const progress = Math.min(1, (now - started) / durationMs);
    overlay.setOpacity(startOpacity + (target - startOpacity) * progress);
    overlay._glOpacityFrame = progress < 1 ? requestAnimationFrame(update) : null;
  };
  overlay._glOpacityFrame = requestAnimationFrame(update);
}

// Other forecast layers fade the new image over the one on screen.
function crossfadeTo(index, fade) {
  if (state.rasterTransition) {
    if (state.rasterTransition.toIndex === index && state.playing) {
      state.rasterTransition.show(1);
      return;
    }
    if (state.rasterTransition.toIndex === index && state.rasterTransition.progress >= 0.999) return;
    state.rasterTransition.remove();
    state.rasterTransition = null;
  }
  if (state.compactFrameLayers && !state.playing) fade = false;
  if (!state.overlays[index]) state.overlays[index] = createAnimationFrameOverlays(index);
  const pane = state.map?.getPane(ANIMATION_PANE);
  // Flush the starting opacity before enabling the transition, including at
  // the loop boundary where this image has already been shown before.
  if (pane) void pane.offsetWidth;
  pane?.classList.toggle("is-instant", !fade);
  const { fadeMs } = speedTiming();
  const duration = fade ? fadeMs : 0;
  state.stack += 1;
  (state.overlays[index] || []).forEach((overlay) => {
    overlay.setZIndex(state.stack);
    animateOverlayOpacity(overlay, 1, duration);
  });
  clearTimeout(state.fadeTimer);
  const hideOthers = () => hideAnimationFramesExcept(state.index);
  if (fade && fadeMs > 0 && state.playing) return;
  if (fade && fadeMs > 0) state.fadeTimer = setTimeout(hideOthers, fadeMs + 60);
  else hideOthers();
}

function showFrame(index, { fade = true, preserveWaveArrows = false, preserveWindArrows = false, preserveCurrentArrows = false } = {}) {
  if (!state.frames.length) return;
  state.index = Math.max(0, Math.min(state.frames.length - 1, index));
  const frameIndex = state.index;
  const context = state.loadContext;
  if (!state.payloads[frameIndex] || !state.preloadedFrames.has(frameIndex)) {
    setGreatLakesStatus("Loading forecast frame…");
    void preloadFrameImages(frameIndex).then(() => {
      if (!animationContextIsCurrent(context) || state.index !== frameIndex) return;
      if (showFrame(frameIndex, { fade, preserveWaveArrows, preserveWindArrows, preserveCurrentArrows }) && state.playing) scheduleNextFrame(true);
    }).catch((error) => reportFrameLoadError(frameIndex, error, context));
    return false;
  }
  const payload = state.payloads[state.index] || {};
  crossfadeTo(state.index, fade);
  if (state.layer === "wind") {
    const display = greatLakesControlValue("wind-display");
    if (state.particles && display === "flow" && payload.points) state.particles.setFields(windParticleFields(payload.points));
    if (!preserveWindArrows) {
      state.previousArrows?.remove();
      state.previousArrows = null;
      state.arrows?.remove();
      state.arrows = display === "arrows" && payload.points?.length ? createWindArrowLayer(state.map, payload.points).addTo(state.map) : null;
    }
  }
  if (state.layer === "currents") {
    if (state.particles && payload.fields?.length) state.particles.setFields(payload.fields);
    if (state.arrows && !preserveCurrentArrows) {
      state.previousArrows?.remove();
      state.previousArrows = null;
      clearTimeout(state.waveArrowTimer);
      state.arrows.clearLayers();
      renderGreatLakesCurrents(payload.data || [], state.map.getZoom(), state.arrows);
    }
  }
  if (state.layer === "waves") {
    const display = greatLakesControlValue("wave-display");
    if (state.particles && display === "flow") state.particles.setFields(waveParticleFields(payload.arrows || []));
    if (!preserveWaveArrows && display === "arrows") {
      state.previousWaveArrows?.remove();
      state.previousWaveArrows = null;
      state.waveArrows?.remove();
      state.waveArrows = (payload.arrows || []).length ? createWaveArrowLayer(state.map, payload.arrows).addTo(state.map) : null;
    } else if (!preserveWaveArrows) {
      state.previousWaveArrows?.remove();
      state.previousWaveArrows = null;
      state.waveArrows?.remove();
      state.waveArrows = null;
    }
  }
  const frame = state.frames[state.index];
  const label = animationFrameLabel(frame.validTime, state.index);
  const slider = document.querySelector("[data-gl-frame]");
  if (slider) {
    slider.value = String(state.index);
    slider.setAttribute("aria-valuetext", label);
  }
  const labelNode = document.querySelector("[data-gl-frame-label]");
  if (labelNode) labelNode.textContent = label;
  const metadata = payload.metadata || {};
  const status = noaaDataStatus([metadata, state.backgroundPayloads?.[state.index]?.metadata]);
  setGreatLakesStatus(status.message, status.error);
  const temperatureMetadata = state.backgroundPayloads?.[state.index]?.metadata || (state.layer === "temperature" ? metadata : null);
  document.dispatchEvent(new CustomEvent("great-lakes-frame-shown", { detail: { layer: state.layer, metadata: { ...metadata, validTime: frame.validTime }, temperatureMetadata, depth: state.depth } }));
  prefetchAhead(state.index);
  return true;
}

function scheduleNextFrame(frameAlreadyShown = false, skipScalarTransition = false) {
  clearTimeout(state.timer);
  cancelAnimationFrame(state.sliderFrame);
  state.sliderFrame = null;
  if (!state.playing || state.frames.length < 2) return;
  if (document.hidden) return;
  const current = state.index;
  const next = (current + 1) % state.frames.length;
  if (!framePayloadIsLoaded(current) || !state.preloadedFrames.has(current)) {
    const context = state.loadContext;
    void preloadFrameImages(current).then(() => {
      if (animationContextIsCurrent(context) && state.playing && state.index === current) scheduleNextFrame(frameAlreadyShown);
    }).catch((error) => reportFrameLoadError(current, error, context));
    return;
  }
  if (!framePayloadIsLoaded(next) || !state.preloadedFrames.has(next)) {
    const context = state.loadContext;
    void preloadFrameImages(next).then(() => {
      if (animationContextIsCurrent(context) && state.playing && state.index === current) {
        scheduleNextFrame(frameAlreadyShown);
      }
    }).catch((error) => reportFrameLoadError(next, error, context));
    return;
  }
  const { frameMs, fadeMs } = speedTiming();
  if (!frameAlreadyShown) showFrame(state.index, { fade: false });
  let scalarTransitionStarted = false;
  if (fadeMs && !skipScalarTransition && canInterpolateRasterPair(current, next)) {
    const active = state.rasterTransition;
    if (active?.fromIndex === current && active?.toIndex === next) {
      scalarTransitionStarted = true;
    } else {
      let prepared = state.preparedRasterTransition;
      if (prepared?.key !== `${current}:${next}`) {
        void prepareScalarRasterTransition(current, next);
        prepared = state.preparedRasterTransition;
      }
      if (prepared?.transition) {
        startScalarRasterTransition(prepared.transition, current, next, frameMs);
        scalarTransitionStarted = true;
      } else if (prepared?.promise) {
        if (!prepared.waiting) {
          prepared.waiting = true;
          const context = state.loadContext;
          void prepared.promise.then((transition) => {
            prepared.waiting = false;
            if (!animationContextIsCurrent(context) || !state.playing || state.index !== current) {
              transition?.remove();
              return;
            }
            scheduleNextFrame(frameAlreadyShown, !transition);
          });
        }
        return;
      } else {
        skipScalarTransition = true;
      }
    }
  }
  const slider = document.querySelector("[data-gl-frame]");
  if (slider) slider.step = "any";
  if (fadeMs && slider) {
    const from = state.index;
    const started = performance.now();
    const advanceSlider = (now) => {
      if (!state.playing) return;
      const progress = Math.min(1, (now - started) / frameMs);
      const position = next > from ? from + (next - from) * progress : state.frames.length - 1;
      slider.value = String(position);
      state.sliderFrame = progress < 1 ? requestAnimationFrame(advanceSlider) : null;
    };
    state.sliderFrame = requestAnimationFrame(advanceSlider);
  } else if (slider) slider.value = String(state.index);
  // Begin blending immediately; hand off to the next frame when the blend
  // finishes, then start the following blend without a hold between frames.
  if (fadeMs) {
    if (!scalarTransitionStarted) crossfadeTo(next, true);
    const payload = state.payloads[next] || {};
    if (state.layer === "currents") state.particles?.setFields(payload.fields || [], { durationMs: frameMs });
    if (state.layer === "waves" && payload.arrows) state.particles?.setFields(waveParticleFields(payload.arrows), { durationMs: frameMs });
    if (state.layer === "wind" && payload.points) state.particles?.setFields(windParticleFields(payload.points), { durationMs: frameMs });
    if (state.layer === "wind" && greatLakesControlValue("wind-display") === "arrows") {
      const previous = state.arrows;
      const incoming = (payload.points || []).length ? createWindArrowLayer(state.map, payload.points) : null;
      incoming?.setOpacity(0);
      incoming?.addTo(state.map);
      if (incoming) requestAnimationFrame(() => incoming.setOpacity(1));
      previous?.setOpacity?.(0);
      setMarkerGroupOpacity(previous, 0);
      state.previousArrows?.remove();
      state.previousArrows = previous || null;
      state.arrows = incoming;
      clearTimeout(state.waveArrowTimer);
      state.waveArrowTimer = setTimeout(() => {
        state.previousArrows?.remove();
        state.previousArrows = null;
        state.waveArrowTimer = null;
      }, frameMs);
    }
    if (state.layer === "waves" && greatLakesControlValue("wave-display") === "arrows") {
      const previous = state.waveArrows;
      const incoming = (payload.arrows || []).length ? createWaveArrowLayer(state.map, payload.arrows) : null;
      incoming?.setOpacity(0);
      incoming?.addTo(state.map);
      if (incoming) requestAnimationFrame(() => incoming.setOpacity(1));
      previous?.setOpacity?.(0);
      state.previousWaveArrows?.remove();
      state.previousWaveArrows = previous || null;
      state.waveArrows = incoming;
      state.waveArrowTimer = setTimeout(() => {
        state.previousWaveArrows?.remove();
        state.previousWaveArrows = null;
        state.waveArrowTimer = null;
      }, frameMs);
    }
    if (state.layer === "currents" && state.arrows) {
      const previous = state.arrows;
      const incoming = L.layerGroup().addTo(state.map);
      renderGreatLakesCurrents(payload.data || [], state.map.getZoom(), incoming, 0);
      requestAnimationFrame(() => setMarkerGroupOpacity(incoming, 1));
      setMarkerGroupOpacity(previous, 0);
      state.previousArrows?.remove();
      state.previousArrows = previous;
      state.arrows = incoming;
      clearTimeout(state.waveArrowTimer);
      state.waveArrowTimer = setTimeout(() => {
        state.previousArrows?.remove();
        state.previousArrows = null;
        state.waveArrowTimer = null;
      }, frameMs);
    }
  }
  state.timer = setTimeout(() => {
    if (!state.playing) return;
    if (document.hidden) return;  // Resumed by visibilitychange.
    state.previousWaveArrows?.remove();
    state.previousWaveArrows = null;
    state.previousArrows?.remove();
    state.previousArrows = null;
    clearTimeout(state.waveArrowTimer);
    state.waveArrowTimer = null;
    const shown = showFrame(next, {
      fade: false,
      preserveWaveArrows: state.layer === "waves" && greatLakesControlValue("wave-display") === "arrows",
      preserveWindArrows: Boolean(fadeMs && state.layer === "wind" && greatLakesControlValue("wind-display") === "arrows"),
      preserveCurrentArrows: state.layer === "currents" && Boolean(state.arrows)
    });
    if (shown) scheduleNextFrame(true);
  }, frameMs);
}

// Exposed for tests.
export const animationState = state;
